import { useEffect, useRef } from 'react';
import axios from 'axios';
import { syncBuyerPush, isPushDeliveryConfirmed } from '../../lib/push';
import {
  POLL_MS, loadSeen, saveSeen, createChimePlayer, isStandalone,
} from '../../lib/buyerNotifications';

/**
 * FABTEXS AI — headless buyer alert layer for the catalogue/chat screen.
 *
 * Mirrors the seller's `NotificationAlerts` (mounted on every dashboard page)
 * for the buyer side. Two jobs:
 *
 *  1. ALERT WHILE THE APP IS OPEN. The buyer bell + chime previously existed
 *     ONLY on BuyerHome (`/me`), but a buyer spends their time on `/c/:id`.
 *     With the app open there, nothing polled and nothing chimed — which is
 *     why "Buyer — app OPEN" produced no notification at all. This polls the
 *     same buyer-scoped feed and alerts once per genuinely-new item, sharing
 *     BuyerHome's persisted seen-set so nothing double-alerts or replays.
 *
 *  2. KEEP THE PUSH SUBSCRIPTION ALIVE. Silent re-sync on every mount heals a
 *     subscription lost to a PWA reinstall / cleared site-data / endpoint
 *     rotation. In the installed PWA the catalogue onboarding sheet is
 *     suppressed by design, so a first-gesture permission request is the only
 *     remaining way that buyer can ever subscribe — without it, closed-app
 *     buyer push is impossible. Browser tabs are untouched: they keep using
 *     the existing onboarding sheet and are never prompted here.
 *
 * Renders nothing. Never throws into the catalogue/chat flow.
 */
export default function BuyerNotificationAlerts({ buyerId, onOpen }) {
  const seen = useRef(loadSeen());
  const primed = useRef(false);          // first poll only seeds the seen-set
  const chimer = useRef(createChimePlayer());
  const onOpenRef = useRef(onOpen);
  const base = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => { onOpenRef.current = onOpen; }, [onOpen]);

  // ── Push subscription: silent self-heal on load ─────────────────────────
  useEffect(() => {
    if (!buyerId) return;
    syncBuyerPush({ buyerId }).catch(() => {});
  }, [buyerId]);

  // ── Audio unlock + (installed-PWA only) permission request on 1st gesture ─
  useEffect(() => {
    if (!buyerId) return undefined;
    const unlock = () => {
      chimer.current.unlock();
      // Only the installed PWA prompts here. In a browser tab the existing
      // onboarding sheet owns the ask, so its UX is unchanged.
      if (isStandalone()) {
        syncBuyerPush({ buyerId, requestPermission: true }).catch(() => {});
      }
    };
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, [buyerId]);

  // ── Poll the buyer-scoped feed; alert once per new item ─────────────────
  useEffect(() => {
    if (!buyerId) return undefined;
    let alive = true;

    // Stand down ONLY when push is PROVEN to be delivering for this buyer
    // (subscription + accepted send + device-confirmed render). A mere
    // subscription object proves nothing, and silently suppressing the in-app
    // banner on that basis is what left users with no notification at all.
    // Defaults to false (fallback ON) and fails closed.
    let pushProven = false;
    const refreshPushOwnership = async () => {
      pushProven = await isPushDeliveryConfirmed({ ownerKind: 'buyer', ownerId: buyerId });
    };

    const systemNotify = (n) => {
      try {
        if (pushProven) return;
        if (!('Notification' in window) || Notification.permission !== 'granted') return;
        const note = new Notification(`🔔 ${n.seller_name || 'New collection'}`, {
          body: n.body || 'New collection is here. Tap to explore.',
          tag: n.id,                       // dedupes at the OS level too
          icon: '/favicon.png',
        });
        note.onclick = () => {
          try {
            window.focus();
            if (onOpenRef.current) onOpenRef.current(n);
            else if (n.url) window.location.assign(n.url);
          } catch (_e) { /* noop */ }
        };
      } catch (_e) { /* noop */ }
    };

    const tick = async () => {
      try {
        await refreshPushOwnership();
        const { data } = await axios.get(`${base}/api/buyer/notifications`,
          { params: { buyer_id: buyerId } });
        if (!alive) return;
        const items = data.items || [];
        const fresh = items.filter((n) => n.id && !n.is_read && !seen.current.has(n.id));
        if (primed.current && fresh.length > 0) {
          chimer.current.play();                     // once per batch (no loop)
          fresh.slice(0, 3).forEach(systemNotify);
        }
        items.forEach((n) => n.id && seen.current.add(n.id));
        saveSeen(seen.current);
        primed.current = true;
      } catch (_e) { /* offline / transient — ignore */ }
    };

    tick();
    const iv = setInterval(tick, POLL_MS);
    return () => { alive = false; clearInterval(iv); };
  }, [buyerId, base]);

  return null;
}
